import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle,
  ExternalLink,
  Loader2,
  Truck,
  PackageCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, money, dateTime } from "@/lib/api";
import {
  Card,
  StatusBadge,
  Btn,
  FieldLabel,
  inputCls,
  textareaCls,
} from "@/components/admin/ui";
import PaymentCard from "@/components/admin/PaymentCard";

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [form, setForm] = useState({
    carrier: "",
    tracking_number: "",
    notes: "",
    fulfillment_status: "unfulfilled",
  });
  const [saving, setSaving] = useState(false);
  const [buyingLabel, setBuyingLabel] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/admin/orders/${id}`).then((r) => {
      if (!active) return;
      setO(r.data);
      setForm({
        carrier: r.data.carrier || "",
        tracking_number: r.data.tracking_number || "",
        notes: r.data.notes || "",
        fulfillment_status: r.data.fulfillment_status,
      });
    });
    return () => {
      active = false;
    };
  }, [id]);

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${id}`, {
        ...form,
        ...patch,
      });
      setO(data);
      setForm((f) => ({ ...f, ...patch }));
      toast.success("Order updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const buyLabel = async () => {
    if (
      !window.confirm(
        "Buy this live Shippo label? Your Shippo account may be charged.",
      )
    )
      return;
    setBuyingLabel(true);
    try {
      const { data } = await api.post(`/admin/shippo/orders/${id}/buy-label`, {
        confirm_purchase: true,
      });
      setO((current) => ({
        ...current,
        ...data,
        fulfillment_status: "label_purchased",
      }));
      toast.success("Shipping label purchased.");
    } catch (error) {
      toast.error(errMsg(error));
    }
    setBuyingLabel(false);
  };

  if (!o) return null;
  const a = o.shipping_address;

  return (
    <div data-testid="admin-order-detail-page">
      <Link
        to="/admin/orders"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-4"
      >
        <ArrowLeft size={15} /> Orders
      </Link>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold" data-testid="order-detail-number">
          Order #{o.number}
        </h1>
        <StatusBadge
          status={o.payment_status}
          testid="order-detail-payment-status"
        />
        <StatusBadge
          status={o.fulfillment_status}
          testid="order-detail-fulfillment-status"
        />
        <span className="text-sm text-zinc-500">{dateTime(o.created_at)}</span>
      </div>
      {!["paid", "refunded", "partially_refunded"].includes(
        o.payment_status,
      ) && (
        <div
          className="mb-4 flex gap-2 items-start rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="order-unpaid-warning"
        >
          <AlertTriangle size={16} className="mt-0.5" /> This order has not been
          paid. Do not ship it.
        </div>
      )}
      {o.stock_issue && (
        <div
          className="mb-4 flex gap-2 items-start rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          data-testid="order-stock-issue"
        >
          <AlertTriangle size={16} className="mt-0.5" /> Some items were already
          out of stock when payment completed. Check your inventory before
          shipping.
        </div>
      )}
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-4">
        <div className="space-y-4">
          <Card title="Items">
            <ul className="divide-y divide-zinc-100">
              {o.items.map((i) => (
                <li
                  key={`${i.product_id}${i.color}`}
                  className="flex items-center gap-3 py-3"
                >
                  <div className="w-12 h-12 rounded-md bg-zinc-100 overflow-hidden flex-shrink-0">
                    {i.photo && (
                      <img
                        src={fileUrl(i.photo)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{i.name}</p>
                    <p className="text-xs text-zinc-500">{i.color}</p>
                  </div>
                  <span className="text-sm text-zinc-600">
                    {money(i.price)} × {i.quantity}
                  </span>
                  <span className="text-sm w-20 text-right">
                    {money(i.price * i.quantity)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="border-t border-zinc-200 mt-2 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-zinc-600">
                <span>Subtotal</span>
                <span>{money(o.subtotal)}</span>
              </div>
              {o.discount_amount > 0 && (
                <div
                  className="flex justify-between text-zinc-600"
                  data-testid="order-detail-discount"
                >
                  <span>
                    Discount · {o.discount?.code || o.discount?.title}
                  </span>
                  <span>−{money(o.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-zinc-600">
                <span>Shipping</span>
                <span>{money(o.shipping_total)}</span>
              </div>
              {o.shipping_discount > 0 && (
                <div
                  className="flex justify-between text-zinc-600"
                  data-testid="order-detail-shipping-discount"
                >
                  <span>
                    Free shipping · {o.discount?.code || o.discount?.title}
                  </span>
                  <span>−{money(o.shipping_discount)}</span>
                </div>
              )}
              {o.tax > 0 && (
                <div className="flex justify-between text-zinc-600">
                  <span>Tax</span>
                  <span>{money(o.tax)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold pt-1">
                <span>Total</span>
                <span data-testid="order-detail-total">{money(o.total)}</span>
              </div>
            </div>
          </Card>
          <Card
            title="Shipping"
            description="Add the tracking number when you send the package."
            testid="order-shipping-card"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <label>
                <FieldLabel>Carrier</FieldLabel>
                <input
                  className={inputCls}
                  placeholder="e.g. USPS, UPS, FedEx"
                  value={form.carrier}
                  onChange={(e) =>
                    setForm({ ...form, carrier: e.target.value })
                  }
                  data-testid="order-carrier-input"
                />
              </label>
              <label>
                <FieldLabel>Tracking number</FieldLabel>
                <input
                  className={inputCls}
                  value={form.tracking_number}
                  onChange={(e) =>
                    setForm({ ...form, tracking_number: e.target.value })
                  }
                  data-testid="order-tracking-input"
                />
              </label>
              <label>
                <FieldLabel>Shipping status</FieldLabel>
                <select
                  className={inputCls}
                  value={form.fulfillment_status}
                  onChange={(e) =>
                    setForm({ ...form, fulfillment_status: e.target.value })
                  }
                  data-testid="order-status-select"
                >
                  <option value="unfulfilled">To ship</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>
              <label className="sm:col-span-2">
                <FieldLabel hint="(only you see this)">Notes</FieldLabel>
                <textarea
                  rows={2}
                  className={textareaCls}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="order-notes-input"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <Btn
                onClick={() => save()}
                disabled={saving}
                data-testid="order-save-button"
              >
                Save
              </Btn>
              {o.payment_status === "paid" &&
                o.fulfillment_status === "unfulfilled" && (
                  <Btn
                    variant="secondary"
                    onClick={() => save({ fulfillment_status: "shipped" })}
                    disabled={saving}
                    data-testid="order-mark-shipped-button"
                  >
                    <Truck size={15} /> Mark as shipped
                  </Btn>
                )}
              {o.fulfillment_status === "shipped" && (
                <Btn
                  variant="secondary"
                  onClick={() => save({ fulfillment_status: "delivered" })}
                  disabled={saving}
                  data-testid="order-mark-delivered-button"
                >
                  <PackageCheck size={15} /> Mark as delivered
                </Btn>
              )}
            </div>
          </Card>
          <Card
            title="Shippo label"
            description="Labels are purchased only after your manual confirmation."
            testid="order-shippo-label-card"
          >
            <div className="text-sm space-y-2">
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Service</span>
                <span>
                  {o.shipping_details?.selected_service ||
                    "Not a live Shippo rate"}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Shipping paid</span>
                <span>{money(o.shipping_total)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">Shipment status</span>
                <span>{o.shipment_status || "Not created"}</span>
              </div>
              {o.tracking_number && (
                <div className="flex justify-between gap-4">
                  <span className="text-zinc-500">Tracking</span>
                  <span className="font-mono text-xs break-all text-right">
                    {o.tracking_number}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {o.label_url && (
                <a
                  href={o.label_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-zinc-200 text-sm font-medium"
                  data-testid="order-shippo-label-link"
                >
                  <ExternalLink size={14} /> View label PDF
                </a>
              )}
              {o.tracking_url && (
                <a
                  href={o.tracking_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-zinc-200 text-sm font-medium"
                  data-testid="order-shippo-tracking-link"
                >
                  <ExternalLink size={14} /> Track package
                </a>
              )}
              {o.payment_status === "paid" &&
                o.shipping_details?.selected_rate_id &&
                !o.shippo_transaction_id && (
                  <Btn
                    variant="secondary"
                    onClick={buyLabel}
                    disabled={buyingLabel}
                    data-testid="order-buy-shippo-label-button"
                  >
                    {buyingLabel && (
                      <Loader2 size={15} className="animate-spin" />
                    )}{" "}
                    Create shipping label
                  </Btn>
                )}
            </div>
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="Customer" testid="order-customer-card">
            <p className="text-sm font-medium">{o.customer.name}</p>
            <a
              href={`mailto:${o.customer.email}`}
              className="text-sm text-sky-700 hover:underline break-all"
            >
              {o.customer.email}
            </a>
            {o.customer.phone && (
              <p className="text-sm text-zinc-600 mt-1">{o.customer.phone}</p>
            )}
          </Card>
          <Card title="Ship to" testid="order-address-card">
            <div className="text-sm text-zinc-700 leading-relaxed">
              <p>{o.customer.name}</p>
              <p>{a.line1}</p>
              {a.line2 && <p>{a.line2}</p>}
              <p>
                {a.city}
                {a.state ? `, ${a.state}` : ""} {a.postal_code}
              </p>
              <p>{a.country}</p>
            </div>
          </Card>
          <PaymentCard
            key={`${o.refunded_amount || 0}`}
            o={o}
            onChange={setO}
          />
        </div>
      </div>
    </div>
  );
}
